<template>
  <full-screen-modal
    id="weight-calculator-modal"
    :bgColor="'white'"
    :hideSubmitButton="true"
    @close-modal="closeModal()"
    @modal-submitted="modalSubmitted()"
  >
    <div slot="header" v-t="'Header'" class="header"></div>
    <div slot="subHeader">
      <p>
        {{$t('BodyText1')}}
        <help-center-link linkKey="pricing/dimensionalWeight">{{$t('HelpCenterLinkText')}}</help-center-link>
        {{$t('BodyText2')}}
      </p>
    </div>
    <div slot="formContent" class="content">
      <div class="form-content">
        <div id="location" class="radio-buttons">
          <label class="form-label">
            {{ $t('DropDownLabel') }}
          </label>
          <sb-radio-button
              :value="1"
              v-model="formData.orderTypeId"
              class="radio-button-wrapper"
          >
              <span class="label">{{ $t('DropDownOption1') }}</span>
          </sb-radio-button>

          <sb-radio-button
              :value="2"
              v-model="formData.orderTypeId"
              class="radio-button-wrapper"
          >
              <span class="label">{{ $t('DropDownOption2') }}</span>
          </sb-radio-button>
        </div>
        <div id="items-table">
          <label class="form-label">{{ $t('ItemTableLabel') }}</label>
          <div class="table">
            <div class="row text-bold">
              <div class="col">{{$t('col1Header')}}</div>
              <div class="col">{{$t('col2Header')}}</div>
              <div class="col">{{$t('col3Header')}}</div>
              <div class="col">{{$t('col4Header')}}</div>
              <div class="col">{{$t('col5Header')}}</div>
              <div class="col">{{$t('col6Header')}}</div>
              <div class="col col-big">{{$t('col7Header')}}</div>
            </div>
            <div class="row" v-for="(item, index) in formData.items" :key="index">
              <div class="col text-bold" id="remove-item-btn">{{$t('rowRef')}} {{ index + 1 }}
                <span v-if="index !== 0" class="text-right">
                  <a @click="modalRemoveItem(index)">
                    <img src="../../assets/images/trash.svg">
                  </a>
                  </span>
              </div>
              <div class="col">
                <sb-field>
                  <sb-number-input
                    v-model="item.itemLength"
                    :min="0"
                    :value="item.itemLength"
                    numberType="wholeNumber"
                  />
                </sb-field>
              </div>
              <div class="col">
                <sb-field>
                  <sb-number-input
                    v-model="item.itemWidth"
                    :min="0"
                    :value="item.itemWidth"
                    numberType="wholeNumber"
                  />
                </sb-field>
              </div>
              <div class="col">
                <sb-field>
                  <sb-number-input
                    v-model="item.itemHeight"
                    :min="0"
                    :value="item.itemHeight"
                    numberType="wholeNumber"
                  />
                </sb-field>
              </div>
              <div class="col">
                <sb-field>
                  <sb-number-input
                    v-model="item.itemWeight"
                    :min="0"
                    :value="item.itemWeight"
                    numberType="wholeNumber"
                  />
                </sb-field>
              </div>
              <div class="col">
                <sb-field>
                  <sb-number-input
                    v-model="item.itemQuantity"
                    :min="0"
                    :value="item.itemQuantity"
                    numberType="wholeNumber"
                  />
                </sb-field>
              </div>
              <div class="col col-big">
                <sb-dropdown
                  v-if="packagePreferences && packagePreferences.length"
                  v-model="item.itemPreference"
                  >
                    <sb-selected-option slot="selection" slot-scope="{ selected }">{{ getSelectedOption(selected) }}</sb-selected-option>
                    <sb-options>
                      <sb-option v-for="option of packagePreferences" :key="option.value" :value="option.value">{{option.text}}</sb-option>
                    </sb-options>
                </sb-dropdown>
              </div>
            </div>
            <div class="row">
              <div class="col" id="add-item-input">
                <a @click="modelAddItem()" class="text-semibold">
                  <img src="../../assets/images/add-item.svg">
                  <span>{{$t('AddItemText')}}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="heads-up">
        <heads-up-panel title="Tip!">
          <p class="text-normal">
            {{ $t('TipHeadsUpParagraph1') }}
          </p>
          <p class="text-normal">
            {{ $t('TipHeadsUpParagraph2Part1') }}
            <help-center-link linkKey="pricing/customPackaging">{{ $t('TipHeadsUpHelpCenterText') }}</help-center-link>
            {{ $t('TipHeadsUpParagraph2Part2') }}
          </p>
          <p class="text-normal last">
            <span class="text-bold">{{ $t('TipHeadsUpParagraph3Part1') }}</span><span>{{ $t('TipHeadsUpParagraph3Part2') }}</span>
          </p>
        </heads-up-panel>
      </div>
    </div>
    <template slot="extraFormButtons">
      <sb-button
        id="modal-submit-btn"
        variation="primary"
        submit="submit"
        @click='modalSubmitted()'
        :disabled="!isFormValid"
      >
        {{$t('SubmitButtonText')}}
      </sb-button>
      <sb-button
        id="modal-reset-btn"
        variation="secondary"
        @click='modalReset()'
      >
        {{$t('ResetButtonText')}}
      </sb-button>
    </template>
    <template v-if="billableWeightEstimate && billableWeightEstimate.reason">
      <div
        slot="bottomOfScreen"
        v-if="billableWeightEstimate.packageFound"
        class="container"
        >
        <br><br>
        <div class="content results-content">
          <div class="form-content">
            <div id="results-table">
              <div class="table">
                <div class="row">
                  <div class="result-row">
                    {{ $t('ResultTopRowTextPart1') }}
                    <span class="text-bold">{{ billableWeightEstimate.billableWeight }} {{ $t('WeightUnit') }}</span>
                    {{ $t('ResultTopRowTextPart2') }}
                    <span class="text-bold">
                      {{ billableWeightEstimate.packageType }}
                      {{ billableWeightEstimate.packageLength }}x{{ billableWeightEstimate.packageWidth }}x{{billableWeightEstimate.packageHeight}}
                    </span>
                  </div>
                </div>
                <div class="row">
                  <div class="result-column">
                    <span class="text-bold">{{ $t('ActualWeight') }} ({{ $t('WeightUnit') }})</span>
                    <div class="result-cell">
                      <div class="result">{{ billableWeightEstimate.totalWeight }}</div>
                      <div class="unit">{{ $t('WeightUnit') }}</div>
                    </div>
                  </div>
                  <div class="result-column border-right">
                    <span class="text-bold">{{ $t('DimensionalWeight') }} ({{ $t('WeightUnit') }})</span>
                    <div class="result-cell">
                      <div class="result">{{ billableWeightEstimate.dimensionalWeight }}</div>
                      <div class="unit">{{ $t('WeightUnit') }}</div>
                    </div>
                  </div>
                  <div class="result-column">
                    <span class="text-bold">{{ $t('BillableWeight') }} ({{ $t('WeightUnit') }})*</span>
                    <div class="result-cell">
                      <div class="result">{{ billableWeightEstimate.billableWeight }}</div>
                      <div class="unit">{{ $t('WeightUnit') }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="footer text-bold">
              {{ $t('FooterText') }}
            </div>
            <br><br>
            <div class="result-buttons">
              <sb-button
                id="modal-gotit-btn"
                variation="secondary"
                @click='modalGotIt()'
              >
                {{$t('GotItButtonText')}}
              </sb-button>
              <span class="text text-bold text-italics">
                {{ $t('FinalNote') }}
              </span>
            </div>
          </div>
          <div class="heads-up">
            <heads-up-panel title="Heads up!">
              <p class="text-normal">
                {{ $t('ResultHeadsUpText1') }}
              </p>
              <p class="text-normal last">
                {{ $t('ResultHeadsUpText2') }}
                <help-center-link linkKey="pricing/dimensionalWeight">{{ $t('ResultHeadsUpHelpCenterText') }}</help-center-link>.
              </p>
            </heads-up-panel>
          </div>
        </div>
      </div>
      <div
        slot="bottomOfScreen"
        v-else
        class="container"
        >
        <div class="content">
          <div class="error-content">
            <div class="text text-bold">
              <span v-if="billableWeightEstimate.reason === 'Considered'">
                {{ $t('GenericErrorMsg') }}
              </span>
              <span v-else>{{ billableWeightEstimate.reason }}</span>
            </div>
          </div>
          <div class="placeholder"></div>
        </div>
      </div>
    </template>
  </full-screen-modal>
</template>

<script>
import HeadsUpPanel from '../HeadsUp'
import FullScreenModal from '@/components/layouts/FullScreenModal'
import { packageTypeEnumMap } from '@/mappers/products/product'
import { isEqual, cloneDeep } from 'lodash'

const defaultOrderTypeId = 0
const defaultItems = {
  itemLength: 0,
  itemWidth: 0,
  itemHeight: 0,
  itemWeight: 0,
  itemQuantity: 0,
  itemPreference: 1
}
const packageTypeIdsToIgnore = ['0', '6']

export default {
  name: 'WeightCalculatorModal',
  components: {
    FullScreenModal,
    HeadsUpPanel
  },
  data: () => ({
    formData: {
      orderTypeId: defaultOrderTypeId,
      items: [{ ...defaultItems }]
    },
    formDataCopy: {},
    result: false
  }),
  computed: {
    packagePreferences () {
      const packagePreferencesArr = []
      for (const key in packageTypeEnumMap) {
        if (!packageTypeIdsToIgnore.includes(key)) {
          packagePreferencesArr.push({
            value: +key,
            text: packageTypeEnumMap[key]
          })
        }
      }

      return packagePreferencesArr
    },
    user () {
      return this.$shipBobStore.getters['user/getUser']
    },
    isFormValid () {
      return !!this.formData.orderTypeId &&
        this.formData.items.length &&
        this.formData.items.every(
          i => i.itemLength > 0 &&
            i.itemWidth > 0 &&
            i.itemHeight > 0 &&
            i.itemWeight > 0 &&
            i.itemQuantity > 0 &&
            !!i.itemPreference
        ) &&
        !isEqual(this.formData, this.formDataCopy)
    },
    billableWeightEstimate () {
      return this.result && this.$shipBobStore.getters['pricing/billableWeightEstimate']
    },
    preservedOriginCountry: {
      get () {
        return this.$shipBobStore.getters['pricing/preservedOriginCountry']
      }
    },
    preservedOriginFC: {
      get () {
        return this.$shipBobStore.getters['pricing/preservedOriginFC']
      }
    }
  },
  methods: {
    async modalSubmitted () {
      this.result = true
      this.formData.userId = this.user.impersonatedUserId || this.user.id
      this.formData.CountryISO = this.preservedOriginCountry
      this.formData.FulfillmentCenterId = this.preservedOriginFC
      this.formDataCopy = cloneDeep(this.formData)
      await this.$shipBobStore.dispatch('pricing/calculatePackageSize', this.formData)
    },
    modelAddItem () {
      this.formData = {
        ...this.formData,
        items: [...this.formData.items, { ...defaultItems }]
      }
    },
    modalRemoveItem (index) {
      this.formData = {
        ...this.formData,
        items: this.formData.items.filter((_, itemIndex) => itemIndex !== index)
      }
    },
    modalReset () {
      this.result = false
      this.formDataCopy = {}
      this.formData = {
        orderTypeId: defaultOrderTypeId,
        items: [{ ...defaultItems }]
      }
    },
    modalGotIt () {
      this.modalReset()
      this.closeModal()
    },
    closeModal () {
      this.$emit('close-modal')
    },
    getSelectedOption (optionValue) {
      return this.packagePreferences.find(pp => pp.value === optionValue).text
    }
  },
  i18n: {
    messages: {
      'en-US': {
        Header: 'Billable Weight Estimator',
        BodyText1: 'Use this calculator to estimate the ',
        HelpCenterLinkText: 'billable weight',
        BodyText2: ' for your B2C orders.',
        AddItemText: 'Add item to this order',
        SubmitButtonText: 'Get Billable Weight Estimate',
        col1Header: 'Order Combo',
        col2Header: 'Length (in)',
        col3Header: 'Width (in)',
        col4Header: 'Height (in)',
        col5Header: 'Weight (oz)',
        col6Header: 'Quantity',
        col7Header: 'Package Preference',
        rowRef: 'Item ',
        ResetButtonText: 'Reset data',
        GotItButtonText: 'Got it',
        DropDownLabel: 'Select type of order:',
        DropDownOption1: 'Domestic Order',
        DropDownOption2: 'International Order',
        ItemTableLabel: 'Enter information for each item in your order:',
        TipHeadsUpParagraph1: `
          Please only include the dimensions of your items. 
          Based on the packaging preference you select, our estimator will factor in the weight of dunnage and packaging.
        `,
        TipHeadsUpParagraph2Part1: 'Packaging Preferences included standard packaging only.',
        TipHeadsUpHelpCenterText: 'Learn more',
        TipHeadsUpParagraph2Part2: 'about our Standard Packaging.',
        TipHeadsUpParagraph3Part1: 'Note',
        TipHeadsUpParagraph3Part2: ': The total weight of all items in your order cannot exceed 50lbs.',
        WeightUnit: 'oz',
        ResultTopRowTextPart1: 'Based on your inputs, the billable weight is',
        ResultTopRowTextPart2: 'and will ship in',
        ActualWeight: 'Actual Weight',
        DimensionalWeight: 'Dimensional Weight',
        BillableWeight: 'Billable Weight',
        FooterText: `
          *For all U.S. orders 16oz or greater, your billable weight estimate uses the larger of dimensional weight vs actual weight.
          Please note, all non-U.S. orders will be subject to dimensional weight.
        `,
        FinalNote: 'Use the billable weight to understand your Fulfillment Cost estimate on the Pricing page.',
        ResultHeadsUpText1: 'This calculator only provides an estimate, and additional factors (e.g., fragile, foldable, etc) may impact your final cost.',
        ResultHeadsUpText2: 'To learn how we calculate dimensional weight,',
        ResultHeadsUpHelpCenterText: 'click here',
        GenericErrorMsg: 'Could not determine package size. Please try a different combination.'
      }
    }
  }
}
</script>

<style lang="scss">
@import './weight-calculator-modal';
</style>